import { PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'

// El plugin nunca vuelve a emitir un token ya entregado: `registration` se dispara por
// PushNotifications.register() y por FirebaseMessaging.onNewToken, y en ambos casos el evento
// pasa una sola vez. Por eso el token se cachea aqui y los listeners viven todo el proceso.
let cachedToken = null
let inFlightRequest = null
let listenersInstalled = false

// Resolvers de las llamadas a getFCMToken() que estan esperando un token.
const pendingResolvers = []

// Callbacks registrados con onFCMTokenChange(), notificados solo cuando el valor cambia.
const tokenSubscribers = new Set()

// Sin tope, un `register()` que nunca emite deja getFCMToken() colgado para siempre Y deja
// inFlightRequest ocupado, bloqueando todos los intentos posteriores de la sesion.
const TOKEN_TIMEOUT_MS = 20000

const settlePending = (settle, value) => {
  const resolvers = pendingResolvers.splice(0)
  resolvers.forEach(entry => settle(entry, value))
}

// Los listeners se instalan UNA vez y NO se remueven nunca. La version anterior los quitaba en
// un `finally` despues de obtener el token: eso dejaba a la app ciega ante cualquier rotacion,
// el backend se quedaba con el token viejo, FCM respondia NotFound y
// FirebaseService::handleInvalidToken borraba la fila, dejando al usuario sin push de forma
// permanente. Al ser un unico par de listeners tampoco hay riesgo de acumularlos.
const installListeners = async () => {
  if (listenersInstalled) return
  listenersInstalled = true

  try {
    await PushNotifications.addListener('registration', ({ value }) => {
      const changed = cachedToken !== value
      cachedToken = value
      settlePending((entry, token) => entry.resolve(token), value)
      if (!changed) return
      tokenSubscribers.forEach(callback => {
        try {
          callback(value)
        } catch (error) {
          console.error(error)
        }
      })
    })

    await PushNotifications.addListener('registrationError', ({ error }) => {
      settlePending((entry, reason) => entry.reject(reason), error)
    })
  } catch (error) {
    // Sin listeners no hay forma de recibir el token; se reintenta en la proxima llamada.
    listenersInstalled = false
    throw error
  }
}

const requestToken = async () => {
  await installListeners()

  const tokenPromise = new Promise((resolve, reject) => {
    const entry = {}
    const timeout = setTimeout(() => {
      // El resolver se saca de la cola para que un token que llegue tarde no intente
      // resolver una promesa ya rechazada.
      const index = pendingResolvers.indexOf(entry)
      if (index !== -1) pendingResolvers.splice(index, 1)
      reject(new Error('Timeout esperando el token de FCM'))
    }, TOKEN_TIMEOUT_MS)

    entry.resolve = token => {
      clearTimeout(timeout)
      resolve(token)
    }
    entry.reject = reason => {
      clearTimeout(timeout)
      reject(reason)
    }

    pendingResolvers.push(entry)
  })

  await PushNotifications.register()
  return tokenPromise
}

export const getFCMToken = async () => {
  if (!Capacitor.isNativePlatform()) return null
  if (cachedToken) return cachedToken
  // Llamadas concurrentes comparten el mismo registro en vuelo (boot, login y el
  // interceptor de la API pueden pedir el token casi al mismo tiempo).
  if (inFlightRequest) return inFlightRequest

  inFlightRequest = requestToken().finally(() => {
    inFlightRequest = null
  })

  return inFlightRequest
}

// Avisa cuando FCM entrega un token distinto al que la app ya conocia. El consumidor debe
// re-registrarlo en el backend: es la unica via para que una rotacion en caliente no deje al
// dispositivo sin push. Devuelve la funcion para darse de baja.
export const onFCMTokenChange = callback => {
  tokenSubscribers.add(callback)
  return () => tokenSubscribers.delete(callback)
}

export const clearFCMToken = () => {
  cachedToken = null
}
